import type { DeliveryState, MeasurementState, Platform } from "@aco/core";
import type { CreativeFormat } from "./creative-format";
import type { BreakdownAssignment } from "./reporting-breakdowns";
import type { ExtraColumnKey } from "./breakdown-columns";

export interface BreakdownRow {
  assignments?: BreakdownAssignment[];
  adId: string | null;
  accountId: string;
  campaignId: string | null;
  adGroupId: string | null;
  name: string;
  platform: Platform;
  objective: string | null;
  format: CreativeFormat;
  // Provider Ad vs Performance Max Asset (non-additive). Defaults to an additive
  // ad; see resolveCreativeEntity on the server.
  entityType: "ad" | "asset";
  additive: boolean;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  costPerConversion: number | null;
  deliveryState: DeliveryState;
  firstSeen: string;
  thumbnailUrl: string | null;
  // Optional playable media source (issue 14). Only creative detail loads it, so
  // no table row ever downloads a video; the table always renders the poster.
  // Null when reporting supplied a poster only.
  previewMediaUrl?: string | null;
  videoStarts: number;
  videoSixSecondViews: number;
  videoP25: number;
  videoP50: number;
  videoP75: number;
  videoCompletions: number;
  averageVideoPlaySeconds: number | null;
  averageVideoPlaySecondsPerUser: number | null;
  likes: number;
  comments: number;
  shares: number;
  follows: number;
  profileVisits: number;
  conversionDetails: Array<{ name: string; value: number; state: MeasurementState }>;
  /** The Company's extra metrics, read apart from the report; null is not measured. */
  extra?: Partial<Record<ExtraColumnKey, number | null>>;
}

export type BreakdownSortKey =
  | "name"
  | "platform"
  | "format"
  | "delivery"
  | "spend"
  | "impressions"
  | "cpm"
  | "clicks"
  | "ctr"
  | "conversions"
  | "cpc"
  | "cvr"
  | "costPerConversion"
  | "firstSeen"
  | ExtraColumnKey
  | `conversion:${string}`;

export interface BreakdownSort {
  key: BreakdownSortKey;
  direction: "asc" | "desc";
}

// Sort keys backed directly by a stored row property (the rest — delivery and
// the derived ratios — are resolved before the fallback lookup below).
type RowBackedSortKey =
  | "name"
  | "platform"
  | "format"
  | "spend"
  | "impressions"
  | "clicks"
  | "conversions"
  | "costPerConversion"
  | "firstSeen";

const EXTRA_COLUMN_KEYS = new Set<string>([
  "reach",
  "frequency",
  "thruplays",
  "landingPageViews",
  "linkClicks",
  "engagement",
]);

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export function defaultBreakdownSortDirection(key: BreakdownSortKey): "asc" | "desc" {
  return key === "name" || key === "platform" || key === "format" ? "asc" : "desc";
}

// Rank for the reporting-derived Delivery column so a sort surfaces delivering
// entities first (desc) and idle ones first (asc).
const DELIVERY_RANK: Record<DeliveryState, number> = { delivering: 2, inactive: 1, none: 0 };

// Derived metrics are computed here (never stored on the row) so sorting and the
// rendered cell always agree. Each returns null where its denominator is zero, so
// an undefined ratio sorts last instead of reading as a real zero.
export function ratePerMille(row: BreakdownRow): number | null {
  return row.impressions > 0 ? (row.spend / row.impressions) * 1000 : null;
}
export function clickThroughRate(row: BreakdownRow): number | null {
  return row.impressions > 0 ? (row.clicks / row.impressions) * 100 : null;
}
export function costPerClick(row: BreakdownRow): number | null {
  return row.clicks > 0 ? row.spend / row.clicks : null;
}
export function postClickConversionRate(row: BreakdownRow): number | null {
  return row.clicks > 0 ? (row.conversions / row.clicks) * 100 : null;
}

function sortValue(row: BreakdownRow, key: BreakdownSortKey): string | number | null {
  if (key.startsWith("conversion:")) {
    const conversionName = key.slice("conversion:".length);
    return row.conversionDetails.find((item) => item.name === conversionName)?.value ?? 0;
  }
  if (EXTRA_COLUMN_KEYS.has(key)) return row.extra?.[key as ExtraColumnKey] ?? null;
  if (key === "delivery") return DELIVERY_RANK[row.deliveryState];
  if (key === "cpm") return ratePerMille(row);
  if (key === "ctr") return clickThroughRate(row);
  if (key === "cpc") return costPerClick(row);
  if (key === "cvr") return postClickConversionRate(row);
  const value = row[key as RowBackedSortKey];
  return typeof value === "string" || typeof value === "number" || value === null ? value : null;
}

export function sortBreakdownRows(rows: BreakdownRow[], sort: BreakdownSort) {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftValue = sortValue(left.row, sort.key);
      const rightValue = sortValue(right.row, sort.key);
      if (leftValue == null && rightValue != null) return 1;
      if (leftValue != null && rightValue == null) return -1;
      if (leftValue == null || rightValue == null) return left.index - right.index;
      const comparison =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : collator.compare(String(leftValue), String(rightValue));
      if (comparison === 0) return left.index - right.index;
      return sort.direction === "asc" ? comparison : -comparison;
    })
    .map(({ row }) => row);
}
